// Ag grid options
import {
  CellKeyDownEvent,
  IRowNode,
  ProcessCellForExportParams,
  SuppressKeyboardEventParams,
  ValueGetterParams,
  ValueSetterParams,
  GridOptions,
} from 'ag-grid-community'
import _ from 'lodash'
import {
  AggregateField,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { intl } from '../../../../i18n'
import {
  ColumnType,
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
  processDataFromClipboard,
} from '../../../containers/commons/AgGrid'
import {
  ServerSideTextFilter,
  TextFilter,
} from '../../../containers/BulkSheetView/components/filter'
import { ServerSideTextFloatingFilter } from '../../../containers/BulkSheetView/components/floatingFilter'
import { filter, ProjectPlanNewRow } from '../projectPlanNew'
import { getLabel } from '../../../../lib/commons/i18nLabel'
import store from '../../../../store'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { requireSave } from '../../../../store/requiredSaveData'
import objects from '../../../../utils/objects'
import { ProjectPlanTreeCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemName/projectPlanTreeCellRenderer'
import {
  ApplicationFunctionPropertyConfiguration,
  CustomEnumValue,
  FunctionProperty,
  PropertyType,
} from '../../../../lib/commons/appFunction'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { SprintDetail, SprintStatus } from '../../../../lib/functions/sprint'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import DateTimeVO from '../../../../domain/value-object/DateTimeVO'
import { FlagxsColumnDef } from '../../../containers/BulkSheetView/gridOptions/extension'
import { normalize } from '../../../../utils/multilineText'
import BoolExpression from '../../../../utils/boolExpression'
import { MutableRefObject, useMemo } from 'react'
import { TAG_DELIMITER } from '../../../../lib/functions/tag'
import useDefaultColumns from './defaultColumns'
import { overwriteColumnByPropertyConfiguration } from '../../../containers/BulkSheetView/gridOptions/propertyConfiguration'
import { useWbsItemAdditionalPropertyColumns } from '../../../containers/BulkSheetView/hooks/wbsItemAdditionalProperties'
import {
  wbsItemAdditionalPropertyValuesVoService,
  WbsItemAdditionalPropertyValueType,
} from '../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { WbsItemAdditionalPropertyEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { excelStyles } from '../../../containers/BulkSheet/excel'
import { dateVoService } from '../../../../domain/value-object/DateVO'
import { DISPLAY_DATE_FORMAT } from '../../../../utils/date'
import TextCell from '../../../containers/commons/AgGrid/components/cell/common/richText/TextCell'
import { GanntEditTargetNodeSelectionProps } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer'
import { WbsItemSelectColumnOptionType } from '../../../hooks/useWbsItemSelectColumnOptions'
import {
  processAutoCompleteCellFromClipboard,
  processMultiSelectNotSuctomEnumCellForClipboard,
  processMultiSelectNotSuctomEnumCellFromClipboard,
  processSelectNotSuctomEnumCellForClipboard,
  processSelectNotSuctomEnumCellFromClipboard,
} from '../../../containers/BulkSheetView/gridOptions/cell/copyAndPaste'
import { PROJECT_PLAN_CELL_CLASS_RULES } from '../../../containers/BulkSheetView/gridOptions/cell/cellClassRules'

export const useProjectPlanNewGridOptions = ({
  projectUuid,
  switchRootWbsItem,
  refreshWbsItem,
  submitSingleRow,
  updateRows,
  addRowToChild,
  propertyConfigurations,
  wbsItemAdditionalProperties,
  onChangeRowHeight,
  openWbsDetailDialog,
  closeWbsDetailDialog,
  ganttEditTargetNodeSelection,
  selectColumnOptions,
  useSeparatedStatusColumn,
  isMilestoneEnabled,
  useSprintsMultiSelect,
}: {
  projectUuid: string
  switchRootWbsItem: (event: MouseEvent, rootProjectPlanUuid?: string) => void
  refreshWbsItem: MutableRefObject<(uuid: string) => void>
  submitSingleRow: MutableRefObject<
    ((uuid: string) => Promise<boolean | undefined>) | undefined
  >
  updateRows?: (rows: ProjectPlanNewRow[]) => void
  addRowToChild: (type: WbsItemTypeVO, parentUuid: string) => void
  propertyConfigurations: ApplicationFunctionPropertyConfiguration[]
  wbsItemAdditionalProperties?: WbsItemAdditionalPropertyLayoutEntity
  onChangeRowHeight?: (value: number) => void
  openWbsDetailDialog?: () => void
  closeWbsDetailDialog?: () => void
  ganttEditTargetNodeSelection?: GanntEditTargetNodeSelectionProps | undefined
  selectColumnOptions?: WbsItemSelectColumnOptionType
  /** ステータス列を分離表示するか（新しいUI） */
  useSeparatedStatusColumn?: boolean
  isMilestoneEnabled?: boolean
  /** スプリント複数選択機能を使用するか（Feature Flag） */
  useSprintsMultiSelect?: boolean
}): GridOptions => {
  const columnDefs = useDefaultColumns({
    projectUuid,
    switchRootWbsItem,
    updateRows,
    addRowToChild,
    onChangeRowHeight,
    ganttEditTargetNodeSelection,
    selectColumnOptions,
    useSeparatedStatusColumn,
    useSprintsMultiSelect,
  }) as FlagxsColumnDef[]
  const {
    wbsItemAdditionalPropertyColumns,
    entityExtensionExternalIdToWbsItemAdditionalPropertyUuid,
  } = useWbsItemAdditionalPropertyColumns<ProjectPlanNewRow>(
    projectUuid,
    wbsItemAdditionalProperties,
    {
      getAdditionalPropertyValues: (rowData: ProjectPlanNewRow) =>
        rowData.body?.wbsItem.additionalPropertyValues,
      setAdditionalPropertyValues: (
        rowNode: IRowNode<ProjectPlanNewRow>,
        wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
        oldValue: WbsItemAdditionalPropertyValueType,
        newValue: WbsItemAdditionalPropertyValueType
      ) => {
        const rowData = rowNode.data
        if (!rowData || !rowData.body) {
          return
        }
        let newAdditionalPropertyValues =
          rowData.body.wbsItem.additionalPropertyValues
        if (!newAdditionalPropertyValues) {
          newAdditionalPropertyValues =
            wbsItemAdditionalPropertyValuesVoService.construct()
        }
        wbsItemAdditionalPropertyValuesVoService.setValue(
          newAdditionalPropertyValues,
          wbsItemAdditionalProperty,
          newValue
        )

        rowData.body.wbsItem.additionalPropertyValues =
          newAdditionalPropertyValues
        rowData.edited = true
        if (!rowData.editedData) {
          rowData.editedData = {}
        }
        let oldAdditionalPropertyValues =
          rowData.editedData.additionalPropertyValues
        if (!oldAdditionalPropertyValues) {
          oldAdditionalPropertyValues =
            wbsItemAdditionalPropertyValuesVoService.construct()
        }
        if (
          !wbsItemAdditionalPropertyValuesVoService.existIn(
            oldAdditionalPropertyValues,
            wbsItemAdditionalProperty.uuid
          )
        ) {
          wbsItemAdditionalPropertyValuesVoService.setValue(
            oldAdditionalPropertyValues,
            wbsItemAdditionalProperty,
            oldValue
          )
          rowData.editedData = {
            ...rowData.editedData,
            additionalPropertyValues: oldAdditionalPropertyValues,
          }
        }
        rowNode.setData(rowData)
        store.dispatch(requireSave())
      },
      getWbsItemType: (rowData: ProjectPlanNewRow) =>
        rowData.body?.wbsItem.wbsItemType,
      columnFilterType: 'SERVER_SIDE',
    },
    propertyConfigurations
  )

  const gridOptions = useMemo(
    () => {
      if (wbsItemAdditionalPropertyColumns.length > 0) {
        const ganttChartColumnIndex = columnDefs.findIndex(column =>
          column.externalId.endsWith('ganttChart')
        )
        columnDefs.splice(
          ganttChartColumnIndex,
          0,
          ...(wbsItemAdditionalPropertyColumns as FlagxsColumnDef[])
        )
      }
      overwriteColumnByPropertyConfiguration({
        columns: columnDefs,
        propertyConfigurations,
        entityExtensionExternalIdToWbsItemAdditionalPropertyUuid,
      })

      const displayNameUiMeta = {
        propertyType: PropertyType.Text,
        maxLength: 256,
        requiredIf: BoolExpression.of(true),
      } as Partial<FunctionProperty>

      return {
        // Grid context. Define grid specific properties
        context: {
          aggregateTarget: WbsItemType.DELIVERABLE,
          aggregateField: AggregateField.WBS_ITEM_WORKLOAD,
          ...selectColumnOptions,
          useSprintsMultiSelect,
        },
        /**
         * contents below could be commonized.
         */
        // Styling
        groupHeaderHeight: 25,
        headerHeight: isMilestoneEnabled ? 65 : 45,
        floatingFiltersHeight: 25,
        rowHeight: 32,
        rowClassRules: {
          'ag-row-copied-or-selected-row': p =>
            p.context.copied?.some(v => v.id === p.node.id),
          'ag-row-copied-single-row': p =>
            p.context.copied?.some(
              v => v.id === p.node.id && v.place === 'both'
            ),
          'ag-row-copied-start-row': p =>
            p.context.copied?.some(
              v => v.id === p.node.id && v.place === 'top'
            ),
          'ag-row-copied-middle-row': p =>
            p.context.copied?.some(
              v => v.id === p.node.id && v.place === 'middle'
            ),
          'ag-row-copied-end-row': p =>
            p.context.copied?.some(
              v => v.id === p.node.id && v.place === 'bottom'
            ),
        },
        // Row grouping
        treeData: true,
        excludeChildrenWhenTreeDataFiltering: true,
        autoGroupColumnDef: {
          field: 'body.wbsItem.displayName',
          headerName: intl.formatMessage({ id: 'projectPlan.displayName' }),
          width: 400,
          pinned: true,
          cellClass: 'sevend-ag-cell-project-plan-new-display-name-cell',
          cellRendererParams: {
            suppressCount: true,
            suppressDoubleClickExpand: true,
            innerRenderer: ProjectPlanTreeCellRenderer,
            onCloseWbsItemDetail: wbsItem => {
              refreshWbsItem.current(wbsItem.uuid)
              closeWbsDetailDialog && closeWbsDetailDialog()
            },
            beforeOpenWbsItemDetail: async uuid => {
              const success = await submitSingleRow.current?.(uuid)
              return success
            },
            onOpenWbsItemDetail: () => {
              openWbsDetailDialog && openWbsDetailDialog()
            },
            uiMeta: displayNameUiMeta,
          },
          editable: true,
          cellEditor: TextCell,
          cellEditorParams: {
            parseInitialValue: (value: string) => {
              return value || ''
            },
            uiMeta: displayNameUiMeta,
          },
          filter: ServerSideTextFilter,
          floatingFilter: true,
          floatingFilterComponent: ServerSideTextFloatingFilter,
          filterParams: {
            fetch: (v: TextFilter) => {
              return filter({ projectUuid, displayName: v })
            },
          },
          valueGetter: (params: ValueGetterParams<ProjectPlanNewRow>) => {
            return params.data?.body?.wbsItem?.displayName ?? ''
          },
          valueSetter: (params: ValueSetterParams<ProjectPlanNewRow>) => {
            const field = params.colDef.field || params.colDef.colId
            if (
              !field ||
              (!params.oldValue && !params.newValue) ||
              params.oldValue === params.newValue
            ) {
              return false
            }
            objects.setValue(params.data, field, params.newValue)
            params.data.edited = true
            if (!params.data.editedData) {
              params.data.editedData = { [field]: params.oldValue }
            } else if (!params.data.editedData.hasOwnProperty(field)) {
              params.data.editedData[field] = params.oldValue
            }

            store.dispatch(requireSave())
            return true
          },
          cellClassRules: {
            'hover-over-can-drop': params => {
              return (
                params.context?.onTree &&
                params.context?.draggableNodeId &&
                params.node?.id === params.context.draggableNodeId
              )
            },
          },
          equals: (valA: string, valB: string) => valA === valB,
        },
        // Row actions
        rowDragManaged: false,
        rowDragMultiRow: true,
        suppressMoveWhenRowDragging: true,
        enterNavigatesVerticallyAfterEdit: true,
        onCellKeyDown: (params: CellKeyDownEvent) => {
          // @ts-ignore
          if (!['Delete', 'Backspace'].includes(params.event.key)) return

          // Do not clear while editing cells,
          // because you will not working undo if you erase text during editing.
          const editingCells = params.api.getEditingCells()
          if (!_.isEmpty(editingCells)) return

          const cellRanges = params.api.getCellRanges() || []
          cellRanges.forEach(range => {
            const start = Math.min(
              range.startRow!.rowIndex,
              range.endRow!.rowIndex
            )
            const end = Math.max(
              range.startRow!.rowIndex,
              range.endRow!.rowIndex
            )
            range.columns.forEach(column => {
              const colDef = column.getColDef()
              if (
                typeof colDef.editable === 'function'
                  ? colDef.editable(params)
                  : colDef.editable
              ) {
                for (let i = start; i <= end; i++) {
                  const rowNode = params.api.getDisplayedRowAtIndex(i)
                  rowNode?.setDataValue(column, undefined)
                }
              }
            })
          })
        },
        // Column definition
        columnTypes: columnTypes(),
        components: frameworkComponents,
        processUnpinnedColumns: () => [],
        defaultColDef: {
          cellDataType: false,
          width: 150,
          editable: false,
          enableValue: false,
          sortable: true,
          resizable: true,
          suppressHeaderMenuButton: true,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          suppressSizeToFit: true,
          singleClickEdit: false,
          cellEditor: 'textEditor',
          cellRenderer: DefaultCellRenderer,
          onCellClicked: defaultOnCellClicked,
          suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
            // When editing in Japanese, any keyboard event should be suppressed.
            if (params.editing && params.event.isComposing) {
              return true
            }
            // Suppress Ctrl+Shift+V (or Cmd+Shift+V on Mac) to let custom moveCutRow handler work
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
            const modKey = isMac ? params.event.metaKey : params.event.ctrlKey
            if (
              modKey &&
              params.event.shiftKey &&
              params.event.key.toLowerCase() === 'v'
            ) {
              return true
            }
            // Prevent editing cell for DELETE or BACKSPACE
            return (
              !params.editing &&
              ['Delete', 'Backspace'].includes(params.event.key)
            )
          },
          valueSetter: (params: ValueSetterParams) => {
            const field = params.colDef.field || params.colDef.colId
            if (
              !field ||
              (!params.oldValue && !params.newValue) ||
              params.oldValue === params.newValue
            ) {
              return false
            }
            objects.setValue(params.data, field, params.newValue)
            params.data.edited = true
            if (!params.data.editedData) {
              params.data.editedData = { [field]: params.oldValue }
            } else if (!params.data.editedData.hasOwnProperty(field)) {
              params.data.editedData[field] = params.oldValue
            }

            store.dispatch(requireSave())
            return true
          },
          cellClassRules: PROJECT_PLAN_CELL_CLASS_RULES,
        },
        columnDefs,
        getRowStyle: params => {
          const w = params.data.body?.wbsItem
          if (w && !w.wbsItemType.isTask()) {
            return { fontWeight: 'bold' }
          }
        },
        // Copy & paste
        processCellForClipboard,
        processCellFromClipboard,
        processDataFromClipboard,
        // Footer
        statusBar: {
          statusPanels: [
            {
              statusPanel: 'totalRowCountStatusPanel',
              align: 'left',
            },
            {
              statusPanel: 'agAggregationComponent',
              statusPanelParams: {
                aggFuncs: ['sum', 'count'],
              },
              align: 'left',
            },
          ],
        },
        localeText: {
          sum: intl.formatMessage({
            id: 'bulksheet.statusPanel.sum',
          }),
          count: intl.formatMessage({
            id: 'bulksheet.statusPanel.count',
          }),
          totalAndFilteredRows: intl.formatMessage({
            id: 'bulksheet.statusPanel.totalAndFilteredRows.title',
          }),
          of: intl.formatMessage({
            id: 'bulksheet.statusPanel.totalAndFilteredRows.division',
          }),
        },
        excelStyles: excelStyles,
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  return gridOptions as GridOptions
}

export const processCellForClipboard = ({
  column,
  value,
  node,
  context,
}: ProcessCellForExportParams) => {
  if (!column) return value
  const colDef = column.getColDef()
  const { field, type, cellEditorParams } = colDef
  if (column.getColId() === 'ag-Grid-AutoColumn') {
    return node?.data?.body?.wbsItem?.displayName ?? value
  }
  if (!value) return value
  if (['action', 'body.deliverableAttachmentSummary'].includes(field!)) {
    return ''
  }
  if (field === 'body.wbsItem.type') {
    return value?.name
  }
  if (field === 'body.wbsItem.ticketType') {
    const wbsItemType = node?.data?.body?.wbsItem.wbsItemType
    return wbsItemType?.isTicket() ? wbsItemType?.name : ''
  }
  if (field === 'body.wbsItem.description') {
    return `"${normalize(value ?? '')}"`
  }
  if (field === 'body.commentSummary.latestComment') {
    return value?.text
  }
  if (field === 'body.wbsItem.watchers') {
    return value?.map(v => v.name).join(',')
  }
  if (field === 'body.wbsItem.sprints') {
    return value?.map(v => v.name).join(',')
  }
  if (field === 'body.wbsItem.tags') {
    return value?.map(v => v.name).join(TAG_DELIMITER)
  }
  if (!type) {
    if (typeof value === 'object') {
      return value.name ?? value.displayName
    }
    return value
  }
  if (type.includes(ColumnType.autocomplete)) {
    return (
      getLabel(value?.nameI18n) ||
      value?.name ||
      value?.displayName ||
      value?.officialName
    )
  }
  if (type.includes(ColumnType.customEnum)) {
    const { customEnumCode } = cellEditorParams
    const options: CustomEnumValue[] = context[customEnumCode] ?? []
    const option = options.find(v => v.value === value)
    return getLabel(option?.nameI18n) || option?.name || ''
  }
  if (type.includes(ColumnType.date)) {
    const dateVO = dateVoService.construct(value)
    return dateVoService.isValid(dateVO)
      ? dateVoService.format(dateVO, DISPLAY_DATE_FORMAT)
      : ''
  }
  if (type.includes(ColumnType.dateTime)) {
    return new DateTimeVO(value).toString()
  }
  if (type.includes(ColumnType.multiLineText)) {
    return `"${normalize(value ?? '')}"`
  }
  if (type.includes(ColumnType.selectNotCustomEnum)) {
    return processSelectNotSuctomEnumCellForClipboard(column, value)
  }
  if (type.includes(ColumnType.multiSelectNotCustomEnum)) {
    return processMultiSelectNotSuctomEnumCellForClipboard(column, value)
  }
  return value
}

export const processCellFromClipboard = ({
  column,
  value,
  context,
  node,
  api,
}: ProcessCellForExportParams) => {
  const colDef = column.getColDef()
  const { field, type, cellEditorParams } = colDef
  if (!value) return value
  if (field === 'body.wbsItem.watchers') {
    const names = value.split(',').filter(v => !!v)
    const options = context['member']
    return options.filter(o => names.includes(o.name))
  }
  // スプリントのクリップボード処理（Feature Flagで分岐）
  if (field === 'body.wbsItem.sprints' && context.useSprintsMultiSelect) {
    // 複数スプリント選択モード
    const team = node?.data?.body?.wbsItem.team
    if (!team) return undefined
    const names = value
      .split(',')
      .map(v => v.trim())
      .filter(v => !!v)
    const matchedSprints: SprintDetail[] =
      context.sprint?.filter(
        (v: SprintDetail) => v.teamUuid === team.uuid && names.includes(v.name)
      ) ?? []
    // FINISHED/CANCELLED のスプリントをチェック
    const finishedSprints = matchedSprints.filter(
      s =>
        s.status === SprintStatus.FINISHED || s.status === SprintStatus.CANCELED
    )
    const validSprints = matchedSprints.filter(
      s =>
        s.status !== SprintStatus.FINISHED && s.status !== SprintStatus.CANCELED
    )
    if (finishedSprints.length > 0) {
      store.dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({
            id: 'sprint.paste.finishedWarning.title',
          }),
          text: intl.formatMessage(
            { id: 'sprint.paste.finishedWarning.text' },
            { names: finishedSprints.map(s => s.name).join(', ') }
          ),
        })
      )
      // 有効なスプリントがない場合は元の値を保持（ペーストをキャンセル）
      if (validSprints.length === 0) {
        return node?.data?.body?.wbsItem?.sprints ?? []
      }
      return validSprints
    }
    return matchedSprints
  }
  if (field === 'body.wbsItem.sprint' && !context.useSprintsMultiSelect) {
    // 単一スプリント選択モード
    const team = node?.data?.body?.wbsItem.team
    if (!team) return undefined
    const name = value.trim()
    return context.sprint?.find(
      (v: SprintDetail) => v.teamUuid === team.uuid && v.name === name
    )
  }
  if (field === 'body.wbsItem.tags') {
    const names = value.split(TAG_DELIMITER).filter(v => !!v)
    const options = context['tag'] ?? []
    return options.filter(v => names.includes(v.name))
  }
  if (!type) return value
  const retVal = processAutoCompleteCellFromClipboard(
    column,
    node,
    value,
    context
  )
  if (!!retVal) return retVal
  if (type.includes(ColumnType.customEnum)) {
    const options: CustomEnumValue[] =
      context[cellEditorParams.customEnumCode] ?? []
    const target = options.find(o =>
      [getLabel(o.nameI18n), o.name].filter(v => !!v).includes(value)
    )
    return target?.value
  }
  // TODO: Store date data in VO instead of string
  // Because the scope of the impact is large and the details of the fix have not yet been decided, we are implementing string conversion as a temporary measure.
  if (type.includes(ColumnType.date)) {
    const dateVO = dateVoService.construct(value)
    return dateVoService.isValid(dateVO) ? dateVoService.format(dateVO) : ''
  }
  if (type.includes(ColumnType.dateTime)) {
    return new DateTimeVO(value).serialize()
  }
  if (type.includes(ColumnType.selectNotCustomEnum)) {
    return processSelectNotSuctomEnumCellFromClipboard(column, value)
  }
  const multiSelectValue = processMultiSelectNotSuctomEnumCellFromClipboard(
    column,
    value
  )
  if (!!multiSelectValue) return multiSelectValue
  return value
}
